import Contract from '../models/contract';

/**
 * Extrait l'immatriculation du véhicule depuis les valFormulaires du contrat
 */
export function getVehicleRegistration(contract: Contract | null | undefined): string | null {
  if (!contract?.valFormulaires) return null;

  const registrationField = contract.valFormulaires.find(
    vf => vf.champFormulaire?.key === 'registration' ||
         vf.champFormulaire?.key === 'immatriculation'
  );

  return registrationField?.valText || null;
}

/**
 * Extrait la marque du véhicule
 */
export function getVehicleBrand(contract: Contract | null | undefined): string | null {
  if (!contract?.valFormulaires) return null;

  const brandField = contract.valFormulaires.find(
    vf => vf.champFormulaire?.key === 'brand' ||
         vf.champFormulaire?.key === 'marque'
  );

  return brandField?.valText || null;
}

/**
 * Extrait le modèle du véhicule
 */
export function getVehicleModel(contract: Contract | null | undefined): string | null {
  if (!contract?.valFormulaires) return null;

  const modelField = contract.valFormulaires.find(
    vf => vf.champFormulaire?.key === 'model' ||
         vf.champFormulaire?.key === 'modele'
  );

  return modelField?.valText || null;
}

/**
 * Extrait le VIN (numéro de série) du véhicule
 */
export function getVehicleVin(contract: Contract | null | undefined): string | null {
  if (!contract?.valFormulaires) return null;

  const vinField = contract.valFormulaires.find(
    vf => vf.champFormulaire?.key === 'vin' ||
         vf.champFormulaire?.key === 'numero_serie' ||
         vf.champFormulaire?.key === 'chassisNumber'
  );

  return vinField?.valText || null;
}

/**
 * Extrait le kilométrage du véhicule
 */
export function getVehicleMileage(contract: Contract | null | undefined): number | null {
  if (!contract?.valFormulaires) return null;

  const mileageField = contract.valFormulaires.find(
    vf => vf.champFormulaire?.key === 'km_start' ||
         vf.champFormulaire?.key === 'mileage' ||
         vf.champFormulaire?.key === 'kilometrage'
  );

  return mileageField?.valText ? parseInt(mileageField?.valText, 10) : (mileageField?.valInt || mileageField?.valDouble || null);
}

/**
 * Extrait la date de première mise en circulation
 */
export function getVehicleFirstRegistrationDate(contract: Contract | null | undefined): Date | null {
  if (!contract?.valFormulaires) return null;

  const dateField = contract.valFormulaires.find(
    vf => vf.champFormulaire?.key === 'entry_into_service' ||
         vf.champFormulaire?.key === 'first_registration_date' ||
         vf.champFormulaire?.key === 'date_premiere_circulation' ||
         vf.champFormulaire?.key === 'firstRegistrationDate'
  );

  if (!dateField) return null;
  if (dateField.valDate) return new Date(dateField.valDate);
  if (dateField.valText && dateField.valText !== 'n/a') return new Date(dateField.valText);
  return null;
}

/**
 * Date d'achat du véhicule.
 * Métier (validé produit) : le contrat est souscrit au moment de l'achat du
 * véhicule, on assimile donc la date d'achat à la date de souscription du
 * contrat (dateSouscription). Il n'existe pas de champ « date d'achat » distinct
 * persisté sur le contrat final.
 */
export function getVehiclePurchaseDate(contract: Contract | null | undefined): Date | null {
  if (!contract?.dateSouscription) return null;
  const d = new Date(contract.dateSouscription);
  return isNaN(d.getTime()) ? null : d;
}

/**
 * Construit une description complète du véhicule
 */
export function getVehicleFullDescription(contract: Contract | null | undefined): string {
  const brand = getVehicleBrand(contract);
  const model = getVehicleModel(contract);
  const registration = getVehicleRegistration(contract);

  const parts: Array<string> = [];
  if (brand) parts.push(brand);
  if (model) parts.push(model);
  if (registration) parts.push(`- ${registration}`);

  return parts.join(' ') || 'Véhicule non renseigné';
}

/**
 * Récupère toutes les informations du véhicule
 */
export function getVehicleInfo(contract: Contract | null | undefined) {
  return {
    registration: getVehicleRegistration(contract),
    brand: getVehicleBrand(contract),
    model: getVehicleModel(contract),
    vin: getVehicleVin(contract),
    mileage: getVehicleMileage(contract),
    firstRegistrationDate: getVehicleFirstRegistrationDate(contract),
    purchaseDate: getVehiclePurchaseDate(contract),
    fullDescription: getVehicleFullDescription(contract),
  };
}

/**
 * Récupère une valeur de formulaire par sa clé
 */
export function getFormFieldValue(
  contract: Contract | null | undefined,
  key: string
): any {
  if (!contract?.valFormulaires) return null;

  const field = contract.valFormulaires.find(
    vf => vf.champFormulaire?.key === key
  );

  if (!field) return null;

  // Retourne la première valeur non nulle
  return field.valText ||
         field.valInt ||
         field.valDouble ||
         field.valDate ||
         field.valCheckbox ||
         field.valTextarea ||
         field.valHeure ||
         field.valListe ||
         null;
}

/**
 * Récupère tous les champs de formulaire sous forme d'objet clé-valeur
 */
export function getAllFormFields(contract: Contract | null | undefined): Record<string, any> {
  if (!contract?.valFormulaires) return {};

  const fields: Record<string, any> = {};

  contract.valFormulaires.forEach(vf => {
    const key = vf.champFormulaire?.key;
    if (!key) return;

    const value = vf.valText ||
                  vf.valInt ||
                  vf.valDouble ||
                  vf.valDate ||
                  vf.valCheckbox ||
                  vf.valTextarea ||
                  vf.valHeure ||
                  vf.valListe;

    if (value !== null && value !== undefined) {
      fields[key] = value;
    }
  });

  return fields;
}